import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Panel } from "@/components/common/Panel";
import { formatDate, daysUntil } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Compass, MapPin, Calendar, Bookmark, Link2, Check } from "lucide-react";
import { AnimationFrame } from "@/components/common/AnimationFrame";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["All", "Saved", "Briefing Opportunities", "Appearance Work", "Drafting Work", "Referral Requests", "Panel Opportunities", "Legal Jobs", "Collaborations", "Training", "Tenders"];

export default function Opportunities() {
  const { db } = useApp();
  const [params] = useSearchParams();
  const focus = params.get("focus");
  const [cat, setCat] = useState("All");
  const [me, setMe] = useState({ saved: [], interested: {} });
  useEffect(() => { axios.get(`${API}/opportunities/me`).then((r) => setMe(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (focus) setTimeout(() => document.querySelector(`[data-testid="opportunity-${focus}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 200); }, [focus, db.opportunities.length]);

  const rows = db.opportunities.filter((o) => cat === "All" || (cat === "Saved" ? me.saved.includes(o.id) : o.category === cat));

  const toggleSave = async (o) => {
    try { const { data } = await axios.post(`${API}/opportunities/${o.id}/save`); setMe((m) => ({ ...m, saved: data.saved ? [...m.saved, o.id] : m.saved.filter((x) => x !== o.id) })); toast.success(data.saved ? "Saved to your list." : "Removed from saved."); }
    catch { toast.error("Could not update."); }
  };
  const express = async (o) => {
    try { const { data } = await axios.post(`${API}/opportunities/${o.id}/interest`, {}); setMe((m) => ({ ...m, interested: { ...m.interested, [o.id]: data.interest } })); toast.success(data.duplicate ? "Interest already recorded." : `Interest recorded (ref ${data.interest.ref}).`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not record interest."); }
  };
  const share = async (o) => {
    const url = `${window.location.origin}/opportunities?focus=${o.id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied: " + url); } catch { toast.info(url); }
  };

  return (
    <div>
      <PageHeader title="Opportunities" subtitle="Discover briefs, appearances, referrals, panels and professional openings." />
      <div className="mb-4 flex flex-wrap gap-1.5">{CATEGORIES.map((c) => <button key={c} onClick={() => setCat(c)} className={cn("rounded-full px-3 py-1 text-xs font-medium", cat === c ? "bg-navy text-white" : "border border-line text-ink-2")} data-testid={`opp-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}{c === "Saved" && me.saved.length > 0 && ` (${me.saved.length})`}</button>)}</div>

      {rows.length === 0 ? <div><AnimationFrame slug="bd-funnel" title="Leads narrowing into retained matters" height={260} maxWidth={420} className="mb-2" /><EmptyState icon={Compass} title={cat === "Saved" ? "Nothing saved yet" : "No opportunities in this category"} message={cat === "Saved" ? "Bookmark an opportunity to keep it here." : "Check back soon — new openings are posted regularly."} /></div> :
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {rows.map((o) => {
            const d = daysUntil(o.deadline);
            const saved = me.saved.includes(o.id);
            const interest = me.interested[o.id];
            return (
              <Panel key={o.id} className={cn("flex flex-col p-4", focus === o.id && "ring-2 ring-copper")} data-testid={`opportunity-${o.id}`}>
                <div className="flex items-start justify-between"><span className="rounded-full bg-copper-light px-2 py-0.5 text-[11px] text-copper">{o.category}</span><button onClick={() => toggleSave(o)} title={saved ? "Remove bookmark" : "Save"} data-testid={`opp-save-${o.id}`}><Bookmark className={cn("h-4 w-4", saved ? "fill-copper text-copper" : "text-ink-muted")} /></button></div>
                <div className="mt-2 font-medium text-ink">{o.title}</div>
                <div className="text-sm text-ink-2">{o.org}</div>
                <div className="mt-2 space-y-1 text-xs text-ink-2"><div className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {o.location} · {o.practiceArea}</div><div className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Apply by {formatDate(o.deadline)} {d != null && d >= 0 && <span className="text-warning">({d}d left)</span>}</div></div>
                <div className="mt-2 text-sm font-medium text-copper">{o.compensation}</div>
                <p className="mt-1 text-xs text-ink-muted">{o.requirement}</p>
                <div className="mt-3 flex gap-1.5">
                  <Button size="sm" className="flex-1" disabled={!!interest} onClick={() => express(o)} data-testid={`opp-interest-${o.id}`}>{interest ? <><Check className="mr-1 h-3.5 w-3.5" />Interest sent · {interest.ref}</> : "Express Interest"}</Button>
                  <Button size="sm" variant="outline" onClick={() => share(o)} data-testid={`opp-share-${o.id}`}><Link2 className="mr-1 h-3.5 w-3.5" />Share</Button>
                </div>
              </Panel>
            );
          })}
        </div>}
    </div>
  );
}
